import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

const httpUrl = z.url({ protocol: /^https?$/ });
const httpOrigin = httpUrl.pipe(
  z.string().refine((value) => {
    const url = new URL(value);
    return (
      url.pathname === "/" &&
      !url.search &&
      !url.hash &&
      !url.username &&
      !url.password
    );
  }),
);
const flag = z.enum(["true", "false"]).optional();

export const createWebEnv = (
  input: Record<string, string | number | boolean | undefined>,
) =>
  createEnv({
    clientPrefix: "VITE_",
    client: {
      VITE_MAINTENANCE_MODE: flag,
      VITE_APP_TITLE: z.string().optional(),
      VITE_APP_ENVIRONMENT: z.enum(["dev", "test", "staging", "prod"]),
      VITE_API_V2_BASE_URL: httpOrigin,
      VITE_TRANSCRIPTION_SOCKET_URL: httpOrigin,
      VITE_TRANSCRIPTION_SOCKET_PATH: z
        .string()
        .regex(/^\/[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*\/?$/)
        .default("/socket.io"),
      VITE_STRIPE_PAGE: httpUrl,
      VITE_STRIPE_TABLE_ID: z.string().min(1),
      VITE_STRIPE_KEY: z.string().startsWith("pk_"),
      VITE_CHROME_EXTENSION_ID: z
        .string()
        .regex(/^[a-p]{32}$/)
        .optional(),
      VITE_TURNSTILE_SITE_KEY: z.string().min(1),
      VITE_DISABLE_PREMIUM_RESTRICTIONS: flag,
      VITE_TRANSCRIPTION_ROUTING_ENABLED: flag,
      VITE_WHATS_NEW_ENABLED: flag,
      VITE_SPEAKER_ROLES_ENABLED: flag,
      VITE_NOTE_REGENERATE_ENABLED: flag,
      VITE_MULTIRECORDING_ENABLED: flag,
      VITE_DICTATION_EVERYWHERE_ENABLED: flag,
      VITE_DICTATION_DEEPGRAM_EN_ENABLED: flag,
      VITE_NON_REALTIME_AUTO_LANGUAGE_ENABLED: flag,
      VITE_APP_REDESIGN_ENABLED: flag,
      VITE_SAVED_VOCABULARY_ENABLED: flag,
      VITE_RECORDING_INSTRUCTIONS_ENABLED: flag,
      VITE_RECORDER_V32_ENABLED: flag,
      VITE_REFERRAL_TEMPLATES_ENABLED: flag,
      VITE_REVIEW_CARD_ENABLED: flag,
      VITE_REVIEW_COPY_ANYWAY_ENABLED: flag,
      VITE_TRANSCRIPT_EDIT_ENABLED: flag,
      VITE_TRANSCRIPT_PLAYER_ENABLED: flag,
      VITE_NOTE_SOURCES_ENABLED: flag,
      VITE_TRANSCRIPT_FLAGS_ENABLED: flag,
      VITE_RECORDING_CONSENT_ENABLED: flag,
      VITE_ASSISTANT_FAST_UPLOADS_ENABLED: flag,
      VITE_PRE_CHART_ENABLED: flag,
      VITE_FORM_COMPLETION_ENABLED: flag,
      VITE_GUIDED_FIRST_VISIT_ENABLED: flag,
    },
    runtimeEnv: { ...input },
    emptyStringAsUndefined: true,
    isServer: false,
    onValidationError: (issues) => {
      throw new Error(
        `Invalid web environment: ${issues.map((issue) => issue.path?.map((part) => (typeof part === "object" ? part.key : part)).join(".")).join(", ")}`,
      );
    },
  });
